import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildExportFilterDisplay,
  type ExportFilterDetailRow,
} from "@/domain/export/formatExportFilters";
import type { ExportJobDetail } from "@/types/api";
import { cn } from "@/utils/cn";

function CriteriaTooltipPanel({ rows }: { rows: ExportFilterDetailRow[] }) {
  return (
    <div className="export-criteria-tooltip min-w-[16rem] max-w-[22rem] rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2.5 shadow-[0_6px_16px_rgba(0,0,0,0.1)]">
      <p className="mb-2 border-b border-[var(--border-soft)] pb-1.5 text-[0.68rem] font-bold uppercase tracking-wide text-[var(--muted)]">
        Filter criteria
      </p>
      <dl className="divide-y divide-[var(--border-soft)]">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3 py-1.5 first:pt-0 last:pb-0"
          >
            <dt className="text-[0.7rem] font-bold uppercase tracking-wide text-[var(--muted)]">
              {row.label}
            </dt>
            <dd className="text-xs font-medium leading-snug text-[var(--text)]">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CriteriaHoverTooltip({
  rows,
  anchorRef,
  open,
  id,
}: {
  rows: ExportFilterDetailRow[];
  anchorRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  id: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const panel = panelRef.current;
    const panelWidth = panel?.offsetWidth ?? 280;
    const panelHeight = panel?.offsetHeight ?? 160;
    const edge = 8;
    const gap = 6;

    let left = rect.left;
    left = Math.min(
      Math.max(edge, left),
      Math.max(edge, window.innerWidth - panelWidth - edge),
    );

    const below = rect.bottom + gap;
    const above = rect.top - gap - panelHeight;
    const fitsBelow = below + panelHeight <= window.innerHeight - edge;
    const top = fitsBelow ? below : Math.max(edge, above);

    setStyle({ top, left });
  }, [open, anchorRef, rows]);

  if (!open || rows.length === 0) return null;

  return createPortal(
    <div
      id={id}
      ref={panelRef}
      role="tooltip"
      className="fixed z-[130] pointer-events-none"
      style={{ top: style.top, left: style.left }}
    >
      <CriteriaTooltipPanel rows={rows} />
    </div>,
    document.body,
  );
}

export function ExportFilterCriteriaCell({ job }: { job: ExportJobDetail }) {
  const display = buildExportFilterDisplay(job);
  const anchorRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const hasTooltip = display.rows.length > 0;

  if (display.headline === "—") {
    return <span className="text-sm text-[var(--muted)]">—</span>;
  }

  return (
    <div
      ref={anchorRef}
      className={cn("relative max-w-[14rem] text-sm", hasTooltip && "cursor-help")}
      onMouseEnter={() => hasTooltip && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => hasTooltip && setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={hasTooltip ? 0 : undefined}
      aria-describedby={open && hasTooltip ? tooltipId : undefined}
    >
      <div className="truncate text-sm text-[var(--text)]">{display.headline}</div>
      {display.subline ? (
        <div className="mt-0.5 truncate text-xs text-[var(--muted)]">{display.subline}</div>
      ) : null}
      {hasTooltip ? (
        <CriteriaHoverTooltip
          rows={display.rows}
          anchorRef={anchorRef}
          open={open}
          id={tooltipId}
        />
      ) : null}
    </div>
  );
}

export function ExportFilterCriteriaSection({ job }: { job: ExportJobDetail }) {
  const display = buildExportFilterDisplay(job);
  if (display.rows.length === 0) {
    return null;
  }

  return (
    <div className="panel border border-[var(--border-soft)] p-4">
      <h3 className="text-sm font-semibold text-[var(--foreground)]">
        How this export was filtered
      </h3>
      <p className="mt-1 text-xs text-[var(--muted)]">
        Criteria used when this ZIP was created — for accounting and audit review.
      </p>
      <dl className="mt-4 space-y-2.5">
        {display.rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-1 gap-0.5 sm:grid-cols-[10rem_1fr] sm:gap-3"
          >
            <dt className="text-sm text-[var(--muted)]">{row.label}</dt>
            <dd className="text-sm font-medium text-[var(--foreground)]">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
